import { useEffect, useRef, useState, useMemo } from "react";
import {
  Box, Heading, Input, VStack, Text, Button, Flex, RadioGroup, Radio,
  Spinner, Badge, Container, Icon, InputGroup, InputLeftElement,
  Stack, Divider, useColorModeValue, HStack, SimpleGrid
} from "@chakra-ui/react";
import { SearchIcon, InfoIcon, TimeIcon } from "@chakra-ui/icons";
import { IoFilterSharp, IoSchoolOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import ApiResponse from "../../services/ApiResponse";
import { Carrera } from "../../services/models/Carrera";
import UnauthorizedPage from "../UnauthorizedPage";
import ServerErrorPage from "../ServerErrorPage";

const MODALIDAD_OPTIONS = ["Todas", "Presencial", "Virtual", "Semipresencial"];

const CarrerasPage = () => {
  const navigate = useNavigate();
  const [response, setResponse] = useState(new ApiResponse<Carrera[]>());
  const [selectedModalidad, setSelectedModalidad] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Estilos de UI Senior
  const glassCard = {
    bg: "rgba(255, 255, 255, 0.05)",
    backdropFilter: "blur(12px)",
    border: "1px solid",
    borderColor: "whiteAlpha.200",
    borderRadius: "2xl",
    transition: "all 0.3s ease",
  };

  useEffect(() => {
    const fetchCarreras = async () => {
      const apiResponse = new ApiResponse<Carrera[]>();
      await apiResponse.useFetch(`carreras`, "GET");
      setResponse(apiResponse);
    };
    fetchCarreras();
  
  }, []);

  // 1. Lógica de Filtrado Optimizada (useMemo es clave aquí para el performance)
  const filteredCarreras = useMemo(() => {
    if (!response.data) return [];
    
    const normalizedSearch = searchTerm.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

    return response.data
      .filter((c) => {
        const matchesSearch = c.nombre.toLowerCase().includes(normalizedSearch) || 
        c.institucion_nombre?.toLowerCase().toLowerCase().includes(normalizedSearch);
        const matchesModalidad = selectedModalidad === "" || c.modalidad === selectedModalidad;
        return matchesSearch && matchesModalidad;
      })
      .sort((a, b) => b.prioridad - a.prioridad);
  }, [searchTerm, selectedModalidad, response.data]);

  if (response.loading) return (
    <Flex justify="center" align="center" h="100vh" bg="blue.900">
      <Spinner size="xl" color="#E9681F" thickness="4px" />
    </Flex>
  );

  if (response.responseCode === 401) return <UnauthorizedPage />;
  if (response.responseCode !== 200 && !response.loading) return <ServerErrorPage />;

  return (
    <Box bgGradient="linear(to-b, blue.800, #0a192f)" minH="100vh" pb={20}>
      <Container maxW="container.xl" pt={10}>
        
        {/* Header Hero */}
        <VStack spacing={2} textAlign="center" mb={12} color="white">
          <Badge bg="#E9681F" color="white" px={4} py={1} borderRadius="full" mb={2}>
            {filteredCarreras.length} Carreras Disponibles
          </Badge>
          <Heading size="2xl" fontWeight="black" letterSpacing="tight">
            ¿Qué vas a estudiar hoy?
          </Heading>
          <Text fontSize="xl" opacity={0.7}>
            Explorá la oferta académica más completa de la región.
          </Text>
        </VStack>

        {/* Buscador Principal */}
        <Box {...glassCard} p={2} mb={10} boxShadow="2xl">
          <InputGroup size="lg">
            <InputLeftElement pointerEvents="none" children={<SearchIcon color="whiteAlpha.500" />} />
            <Input
              placeholder="Escribí el nombre de la carrera o institución..."
              variant="unstyled"
              p={4}
              fontSize="lg"
              color="white"
              _placeholder={{ color: "whiteAlpha.400" }}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Box>

        <Flex direction={{ base: "column", lg: "row" }} gap={8}>
          
          {/* Sidebar de Filtros */}
          <Box w={{ base: "full", lg: "280px" }}>
            <VStack {...glassCard} p={6} align="stretch" spacing={6} position="sticky" top="100px">
              <HStack color="#E9681F">
                <Icon as={IoFilterSharp} />
                <Text fontWeight="bold" fontSize="lg">Filtros</Text>
              </HStack>
              
              <Divider borderColor="whiteAlpha.200" />
              
              <Box>
                <Text fontWeight="bold" mb={4} fontSize="sm" color="whiteAlpha.600" textTransform="uppercase">
                  Modalidad
                </Text>
                <RadioGroup onChange={setSelectedModalidad} value={selectedModalidad}>
                  <Stack spacing={3}>
                    {MODALIDAD_OPTIONS.map((opt) => (
                      <Radio 
                        key={opt} 
                        value={opt === "Todas" ? "" : opt} 
                        colorScheme="orange"
                        size="md"
                      >
                        <Text color="white" fontSize="md">{opt}</Text>
                      </Radio>
                    ))}
                  </Stack>
                </RadioGroup>
              </Box>
            </VStack>
          </Box>

          {/* Listado de Resultados */}
          <Box flex="1">
            <SimpleGrid columns={1} spacing={4}>
              {filteredCarreras.length > 0 ? (
                filteredCarreras.map((carrera) => (
                  <CarreraCard 
                    key={carrera.id} 
                    carrera={carrera} 
                    onClick={() => navigate(`/carreras/${carrera.id}`)} 
                  />
                ))
              ) : (
                <Flex {...glassCard} p={20} direction="column" align="center" justify="center">
                  <Icon as={SearchIcon} boxSize={10} mb={4} color="whiteAlpha.300" />
                  <Text fontSize="xl" color="whiteAlpha.500">No encontramos resultados para esa búsqueda.</Text>
                </Flex>
              )}
            </SimpleGrid>
          </Box>
        </Flex>
      </Container>
    </Box>
  );
};

// Sub-componente Card (Senior Clean Code)
const CarreraCard = ({ carrera, onClick }: { carrera: Carrera, onClick: () => void }) => {
  return (
    <Box
      onClick={onClick}
      cursor="pointer"
      bg="rgba(255, 255, 255, 0.03)"
      backdropFilter="blur(8px)"
      border="1px solid"
      borderColor="whiteAlpha.100"
      p={6}
      borderRadius="2xl"
      transition="all 0.3s"
      _hover={{
        bg: "rgba(255, 255, 255, 0.08)",
        transform: "translateX(10px)",
        borderColor: "#E9681F",
      }}
    >
      <Flex justify="space-between" align={{ base: "start", md: "center" }} direction={{ base: "column", md: "row" }} gap={4}>
        <VStack align="start" spacing={1} flex="1">
          <HStack spacing={2}>
            <Badge bg="#E9681F" color="white" fontSize="xs" borderRadius="md">
              {carrera.modalidad}
            </Badge>
            <Badge variant="outline" color="whiteAlpha.700" fontSize="xs">
              {carrera.tipo}
            </Badge>
          </HStack>
          <Heading size="md" color="white" letterSpacing="tight">
            {carrera.nombre}
          </Heading>
          <HStack color="orange.300" spacing={1}>
            <Icon as={IoSchoolOutline} />
            <Text fontWeight="bold" fontSize="sm">
  {carrera?.institucion_nombre}
</Text>
          </HStack>
          <HStack spacing={6} pt={2}>
            <HStack color="whiteAlpha.500" fontSize="xs">
              <TimeIcon />
              <Text>{carrera.duracion_anios} años</Text>
            </HStack>
          </HStack>
        </VStack>
        
        <Button
          bg="#E9681F"
          color="white"
          _hover={{ bg: "#c85617" }}
          rightIcon={<InfoIcon />}
          size="md"
          px={8}
          borderRadius="xl"
        >
          Ver Carrera
        </Button>
      </Flex>
    </Box>
  );
};

export default CarrerasPage;